import { forwardRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Badge, Avatar } from "@mui/material";
//
import { CustomAvatarProps } from "./types";
import { getCharAtName } from "../../utils/util";

// ----------------------------------------------------------------------

const getColorByName = (name: string) => {
  if (["A", "N", "H", "L", "Q"].includes(getCharAtName(name))) return "primary";
  if (["F", "G", "T", "I", "J"].includes(getCharAtName(name))) return "info";
  if (["K", "D", "Y", "B", "O"].includes(getCharAtName(name))) return "success";
  if (["P", "E", "R", "S", "U"].includes(getCharAtName(name))) return "warning";
  if (["V", "W", "X", "M", "Z"].includes(getCharAtName(name))) return "error";
  return "default";
};

// ----------------------------------------------------------------------

const CustomAvatar = forwardRef<HTMLDivElement, CustomAvatarProps>(
  ({ color, name = "", BadgeProps, children, sx, ...other }, ref) => {
    const theme = useTheme();

    const charAtName = getCharAtName(name!!);

    const colorByName = getColorByName(name!!);

    const isHex = typeof color === "string" && color.startsWith("#");
    const colr = color || colorByName;
    const contrast = (hex: string) => {
      const h = hex?.replace("#", "");
      if (h?.length === 6) {
        const r = parseInt(h.substring(0, 2), 16);
        const g = parseInt(h.substring(2, 4), 16);
        const b = parseInt(h.substring(4, 6), 16);
        const yiq = (r * 299 + g * 587 + b * 114) / 1000;
        return yiq >= 128 ? "#000" : "#fff";
      }
      return "#fff";
    };

    const renderContent = isHex ? (
      <Avatar
        ref={ref}
        sx={{
          backgroundColor: color as string,
          color: contrast(color as string),
          fontWeight: theme.typography.fontWeightMedium,
          ...sx,
        }}
        {...other}
      >
        {name && charAtName}
        {children}
      </Avatar>
    ) : colr === "default" ? (
      <Avatar ref={ref} sx={sx} {...other}>
        {name}
        {children}
      </Avatar>
    ) : (
      <Avatar
        ref={ref}
        sx={{
          color: (theme.palette as any)[colr]?.contrastText,
          backgroundColor: (theme.palette as any)[colr]?.main,
          fontWeight: theme.typography.fontWeightMedium,
          ...sx,
        }}
        {...other}
      >
        {name && charAtName}
        {children}
      </Avatar>
    );

    return BadgeProps ? (
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        {...BadgeProps}
      >
        {renderContent}
      </Badge>
    ) : (
      renderContent
    );
  }
);

export default CustomAvatar;
